import { useCallback, useEffect, useState } from 'react'
import { useMemberPortalNotificationsApi } from '@/members-portal/api/notifications'

export function useNotifications({ clubCode }) {
  const [notifications, setNotifications] = useState([])
  const [isLoadingInitial, setIsLoadingInitial] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isClearingAll, setIsClearingAll] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [nextCursor, setNextCursor] = useState(null)
  const [serverUnreadCount, setServerUnreadCount] = useState(0)
  const { fetchMemberPortalNotificationsPage, markNotificationsRead, clearAllNotifications } = useMemberPortalNotificationsApi()

  const load = useCallback(async () => {
    if (!clubCode) return
    setIsLoadingInitial(true)
    try {
      const res = await fetchMemberPortalNotificationsPage(clubCode)
      const list = Array.isArray(res?.data) ? res.data : []
      setNotifications(list)
      setHasMore(Boolean(res?.hasMore))
      setNextCursor(res?.nextCursor || null)
      setServerUnreadCount(Number(res?.unreadCount) || 0)
    } catch (err) {
      console.warn('[member portal] failed to load notifications', err)
    } finally {
      setIsLoadingInitial(false)
    }
  }, [clubCode, fetchMemberPortalNotificationsPage])

  const loadMore = useCallback(async () => {
    if (!clubCode || !hasMore || !nextCursor || isLoadingInitial || isLoadingMore) return
    setIsLoadingMore(true)
    try {
      const res = await fetchMemberPortalNotificationsPage(
        clubCode,
        { cursorCreatedAt: nextCursor.createdAt, cursorId: nextCursor.id }
      )
      const incoming = Array.isArray(res?.data) ? res.data : []

      setNotifications((prev) => {
        const seen = new Set(prev.map((item) => item.id))
        const dedupedIncoming = incoming.filter((item) => item?.id && !seen.has(item.id))
        return [...prev, ...dedupedIncoming]
      })
      setHasMore(Boolean(res?.hasMore))
      setNextCursor(res?.nextCursor || null)
      setServerUnreadCount(Number(res?.unreadCount) || 0)
    } catch (err) {
      console.warn('[member portal] failed to load more notifications', err)
    } finally {
      setIsLoadingMore(false)
    }
  }, [clubCode, fetchMemberPortalNotificationsPage, hasMore, isLoadingInitial, isLoadingMore, nextCursor])

  const markRead = useCallback(
    async (notificationId) => {
      if (!clubCode || !notificationId) return
      try {
        const result = await markNotificationsRead([notificationId], clubCode)
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId
              ? { ...n, is_read: true, isRead: true, read_at: n.read_at || n.readAt || new Date().toISOString() }
              : n
          )
        )
        if ((Number(result?.updatedCount) || 0) > 0) {
          setServerUnreadCount((prev) => Math.max(0, prev - (Number(result?.updatedCount) || 0)))
        }
      } catch (err) {
        console.warn('[member portal] failed to mark notification read', err)
      }
    },
    [clubCode, markNotificationsRead]
  )

  const clearAll = useCallback(async () => {
    if (!clubCode || isClearingAll) return false
    setIsClearingAll(true)
    try {
      await clearAllNotifications(clubCode)
      const readAt = new Date().toISOString()
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          is_read: true,
          isRead: true,
          read_at: n.read_at || n.readAt || readAt,
          readAt: n.readAt || n.read_at || readAt,
        }))
      )
      setServerUnreadCount(0)
      return true
    } catch (err) {
      console.warn('[member portal] failed to clear notifications', err)
      return false
    } finally {
      setIsClearingAll(false)
    }
  }, [clearAllNotifications, clubCode, isClearingAll])

  const unreadCount = serverUnreadCount

  useEffect(() => {
    load()
  }, [load])

  return {
    notifications,
    unreadCount,
    isLoading: isLoadingInitial,
    isLoadingMore,
    hasMore,
    isClearingAll,
    reload: load,
    markRead,
    clearAll,
    loadMore,
  }
}
